import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SignaturePad } from "./SignaturePad";

export default function AdoptSignatureCard({ onAdopted }) {
  const [sig, setSig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState("");
  const [preview, setPreview] = useState("");
  const [drawn, setDrawn] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const debRef = useRef(null);

  const load = () => api.get("/my/signature").then((r) => setSig(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!editing) return undefined;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      if (!typed.trim()) { setPreview(""); return; }
      try {
        const r = await api.get("/my/signature/preview", { params: { typed_name: typed }, responseType: "blob" });
        setPreview(URL.createObjectURL(r.data));
      } catch (e) { /* preview is best-effort */ }
    }, 350);
    return () => clearTimeout(debRef.current);
  }, [typed, editing]);

  const save = async () => {
    if (!typed.trim()) { toast.error("Type your full legal name"); return; }
    if (!consent) { toast.error("Please agree to the electronic signature consent first"); return; }
    setBusy(true);
    try {
      await api.post("/my/signature", { typed_name: typed, drawn_image: drawn || undefined, consent: true });
      toast.success("Signature adopted — signing is one click from now on");
      setEditing(false); setConsent(false); setDrawn(""); setTyped("");
      load();
      onAdopted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="adopt-signature-card">
      <h2 className="font-heading text-lg font-semibold text-stone-900">My signature</h2>
      <p className="text-xs text-stone-500">
        Adopt your signature once — a clean, legible version (plus your handwritten scribble if you want it saved).
        Every time you sign, the date, time, device and IP address are recorded as proof.
      </p>
      {sig?.adopted && !editing ? (
        <div className="mt-4" data-testid="adopted-signature-view">
          <div className="inline-block rounded-lg border border-stone-200 bg-[#fdfcf9] px-6 py-3">
            {sig.pretty_png
              ? <img src={sig.pretty_png} alt={`Adopted signature of ${sig.typed_name}`} className="h-16" data-testid="adopted-signature-image" />
              : <p className="font-heading text-2xl italic">{sig.typed_name}</p>}
          </div>
          <p className="mt-2 text-xs text-stone-500" data-testid="adopted-signature-date">
            Adopted {String(sig.adopted_at).slice(0, 10)}{sig.has_drawn ? " · handwritten version also saved" : ""}
          </p>
          <button onClick={() => { setEditing(true); setTyped(sig.typed_name); }}
            className="mt-2 text-xs font-semibold text-primary underline" data-testid="adopt-signature-readopt">
            Re-adopt / change my signature
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Type your full legal name</span>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="e.g. Kelly M. Hilty"
              className="mt-1 border-stone-300" data-testid="adopt-typed-name" />
          </label>
          {preview && (
            <div className="rounded-lg border border-stone-200 bg-[#fdfcf9] px-6 py-3" data-testid="adopt-preview-wrap">
              <p className="text-[10px] uppercase tracking-wide text-stone-400">This is how it will sign</p>
              <img src={preview} alt="Signature preview" className="mt-1 h-16" data-testid="adopt-preview" />
            </div>
          )}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Your scribble too (optional — saved as proof alongside the legible one)</p>
            <div className="mt-1 rounded-lg border border-stone-200" data-testid="adopt-draw-wrap">
              <SignaturePad onChange={(img) => setDrawn(img || "")} />
            </div>
          </div>
          <label className="flex items-start gap-2.5 rounded-lg border border-stone-200 px-3 py-2.5" data-testid="adopt-consent-row">
            <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="adopt-consent" />
            <span className="text-xs text-stone-600">
              I understand that the electronic signature I have selected is my electronic signature.
              I intend to use it to sign documents electronically within Silver Home Care's system.
            </span>
          </label>
          <Button onClick={save} disabled={busy || !typed.trim() || !consent}
            className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="adopt-save">
            {busy ? "Saving…" : "Adopt this signature"}
          </Button>
        </div>
      )}
    </div>
  );
}
